import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Send, Loader2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND, whatsappLink } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Fahrdienstleistungen",
  "Fahrzeugüberführungen",
  "Fahrzeugreinigung",
  "Fahrzeugpflege",
  "Hol- & Bringservice",
  "TÜV Fahrten",
  "Sonstiges",
];

const reveal = {
  hidden: { y: 40, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

const emptyForm = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        service: form.service || null,
      });
      toast.success("Vielen Dank! Wir melden uns schnellstmöglich bei Ihnen.");
      setForm(emptyForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Senden fehlgeschlagen. Bitte versuchen Sie es erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        {/* Left */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
        >
          <div className="mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-[#0052ff]" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#94a3b8]">
              Kontakt
            </span>
          </div>
          <h2 className="font-display text-5xl sm:text-7xl font-extrabold tracking-tight text-white leading-[0.95]">
            LET&apos;S
            <br />
            <span className="text-[#0052ff]">TALK.</span>
          </h2>
          <p className="mt-6 max-w-md text-[#94a3b8] leading-relaxed">
            Erzählen Sie uns von Ihrem Anliegen — wir erstellen Ihnen ein
            passendes Angebot. Am schnellsten erreichen Sie uns per WhatsApp.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="contact-email"
              className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-300 hover:border-[#0052ff]/50"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#0052ff]">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#94a3b8]">
                  E-Mail
                </div>
                <div className="text-white group-hover:text-[#0052ff] transition-colors">
                  {BRAND.email}
                </div>
              </div>
            </a>

            <a
              href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
              data-testid="contact-phone"
              className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-300 hover:border-[#0052ff]/50"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[#0052ff]">
                <Phone className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#94a3b8]">
                  Telefon
                </div>
                <div className="text-white group-hover:text-[#0052ff] transition-colors">
                  {BRAND.phone}
                </div>
              </div>
            </a>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="group flex items-center gap-4 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 p-5 transition-colors duration-300 hover:bg-[#25D366]/10"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#25D366]/40 text-[#25D366]">
                <FaWhatsapp className="h-5 w-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#94a3b8]">
                  WhatsApp
                </div>
                <div className="text-white">Direkt schreiben</div>
              </div>
            </a>
          </div>
        </motion.div>

        {/* Right — glass form */}
        <motion.form
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl p-8 lg:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
        >
          <div className="grid gap-5">
            <div className="grid gap-2">
              <Label htmlFor="name" className="text-[#94a3b8]">
                Name *
              </Label>
              <Input
                id="name"
                data-testid="input-name"
                value={form.name}
                onChange={update("name")}
                placeholder="Ihr Name"
                className="bg-black/40 border-white/10 text-white placeholder:text-[#52525b] focus-visible:ring-[#0052ff] h-12"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-[#94a3b8]">
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="name@email.de"
                  className="bg-black/40 border-white/10 text-white placeholder:text-[#52525b] focus-visible:ring-[#0052ff] h-12"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone" className="text-[#94a3b8]">
                  Telefon *
                </Label>
                <Input
                  id="phone"
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+49 …"
                  className="bg-black/40 border-white/10 text-white placeholder:text-[#52525b] focus-visible:ring-[#0052ff] h-12"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-[#94a3b8]">Leistung</Label>
              <Select value={form.service} onValueChange={update("service")}>
                <SelectTrigger
                  data-testid="select-service"
                  className="bg-black/40 border-white/10 text-white h-12 focus:ring-[#0052ff]"
                >
                  <SelectValue placeholder="Leistung auswählen" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  {SERVICES.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`service-option-${s}`}
                      className="focus:bg-[#0052ff] focus:text-white"
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="message" className="text-[#94a3b8]">
                Nachricht *
              </Label>
              <Textarea
                id="message"
                data-testid="input-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Wie können wir Ihnen helfen?"
                rows={5}
                className="bg-black/40 border-white/10 text-white placeholder:text-[#52525b] focus-visible:ring-[#0052ff] resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#0052ff] px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#0043cf] disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {loading ? "Wird gesendet…" : "Anfrage senden"}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
